/**
 * How this kitchen is played — by playing it.
 *
 * The shape is `MicCheck` and `LatencyGate` exactly — a card, a step line, a
 * heading, a paragraph, and the actions at the bottom — because a screen that
 * looks like the gates is a screen nobody has to learn. The decisions are all in
 * `tutorialSteps.ts`; this file is presentation, like every other gate component
 * in `flow/`.
 *
 * There is no Next button on the interactive variant, and that is the change
 * that mattered: a card offering "next" beside "do the thing" is a card that
 * teaches nobody, because pressing a button is always cheaper than learning a
 * control. The only decision it ever asks for is the one at the end. What
 * replaces the buttons is an instruction naming the key, and a dim over
 * everything the step is not about.
 *
 * It teaches by drawing the real kitchen. Every picture on it goes through
 * `IconCanvas` calling `drawStation`, `drawIngredient`, `drawContainer`,
 * `drawPlate`, `drawExtinguisher` and `drawFlame` — the identical functions the
 * canvas calls fifty times a second during a round — so a player who reads this
 * has seen the actual hob, the actual burnt pan and the actual fire, not an
 * illustration of them that will drift the first time an icon changes.
 *
 * Several states in this game say what they are with *motion*: a flame
 * flickers, a burner glows in and out, water ripples in the sink, a plate
 * crosses the pass on its way out, a cooking bar fills toward its notch. Prose cannot show any of
 * that, so the animated steps run a `requestAnimationFrame` loop that advances
 * an `atTick` ref. The ref is the fine-grained value the drawing reads; React
 * only sees a quarter-rate signature bump, which is what keeps the phase-5 rule
 * — no 50 Hz value through React — true on a screen with pictures that move.
 * Under `prefers-reduced-motion` the loop never starts and every icon is drawn
 * at tick zero.
 *
 * It sits on the landing page now, before any join at all. It used to sit after
 * the seat was claimed, because a seat existing while the player read was what
 * gave `startRoundIfReady`'s setup hold something to wait on — but the round
 * clock now arms only once the room is full *and* every seat has cleared setup,
 * so a learner who never joins cannot leave a partner waiting in a lobby with
 * nothing to wait for. Learning to play and holding a seat are separate things
 * again, which is what lets the lesson be as long as it needs to be.
 *
 * ## The sandbox
 *
 * In its `gate` variant this is no longer a slideshow. A real `GameState` runs
 * here — `@overcooked/engine`'s `tick`, over a kitchen `tutorialMap.ts` holds as
 * a literal — driven by the same keys the round wants and painted by
 * `render/loop.ts`, the same painter the live canvas uses. A step ends when the
 * engine emits the event that step is teaching: the player chopped something,
 * or they did not. Nobody arrives in a scored round having read about fire.
 *
 * The clock and the keys are this file's; the rules are `tutorialSandbox.ts`'s,
 * which is pure and tested. The frames pushed into the `EventFeed` follow the
 * server's own cadence — every tick's events, a keyframe every
 * `KEYFRAME_INTERVAL_TICKS`, and one extra whenever a step's `setup` changes the
 * kitchen out of band — so the renderer sees exactly the shape of traffic it
 * sees in a round.
 *
 * The `lobby` variant is untouched, and stays a reading card: it is shown while
 * waiting for the other chef, about the kitchen actually being played, and
 * nothing on it should take a keypress away from the lobby.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  Cell,
  Container,
  DishSummary,
  GameEvent,
  Plate,
  PlayerAction,
  Station,
  StationType,
} from '@overcooked/shared'
import { KEYFRAME_INTERVAL_TICKS, TICK_MS } from '@overcooked/shared'
import { serialise } from '@overcooked/engine'
import { EventFeed } from '../feed/feed'
import { startRenderer } from '../render/loop'
import type { Catalog } from '../render/catalog'
import { IconCanvas } from '../render/IconCanvas'
import { drawContainer, drawExtinguisher, drawFlame, drawIngredient, drawPlate } from '../render/icons'
import { drawStation } from '../render/stations'
import { RouteIcon, stationName } from '../render/RouteIcon'
import { RECIPES_AUTO_OPEN_KEY } from '../render/RecipePanel'
import { routeKey, routesFor } from '../render/route'
import { COLOURS, roundRect } from '../render/theme'
import { PracticeRound } from './PracticeRound'
import type { DrawSpec, TutorialStep } from './tutorialSteps'
import { focusCells } from './tutorialSteps'
import type { Sandbox } from './tutorialSandbox'
import { SANDBOX_KEYS, SANDBOX_PLAYER_ID, advanceSandbox, createSandbox, goToStep } from './tutorialSandbox'

/**
 * "This player has been shown how to play."
 *
 * `localStorage`, not a server record: there are no accounts here, and a new
 * browser profile being a new player is the honest reading of a preference this
 * cheap. Every access is wrapped because a locked-down profile throws on the
 * property itself, and a tutorial preference is not worth a blank page.
 */
export const TUTORIAL_SEEN_KEY = 'overcooked.tutorial.seen'

export function hasSeenTutorial(): boolean {
  try {
    return window.localStorage.getItem(TUTORIAL_SEEN_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Remember the visit, and set the recipe panel's default from how it ended.
 *
 * Somebody who read the tutorial through gets the panel up at the first
 * whistle; somebody who pressed "skip — I know this" does not, because they
 * just said they do not need it. One decision, made once, in the one place the
 * player expressed it.
 */
export function rememberTutorial(skipped: boolean): void {
  try {
    window.localStorage.setItem(TUTORIAL_SEEN_KEY, '1')
    window.localStorage.setItem(RECIPES_AUTO_OPEN_KEY, skipped ? '0' : '1')
  } catch {
    // A profile that refuses storage still gets to play; it just gets the
    // tutorial again next time, which is the safe side to fail on.
  }
}

export interface TutorialProps {
  steps: TutorialStep[]
  catalog: Catalog
  /** For the last step's route strip: the same dish summaries the HUD uses. */
  dishes: DishSummary[]
  /**
   * `gate` is step 3 of 3 and ends in a decision. `lobby` is the same cards
   * with no decision attached, filling the wait for the other chef so that time
   * is not dead.
   */
  variant?: 'gate' | 'lobby'
  /**
   * Which gate of how many this is, when it is a gate. Solo skips the mic
   * check, so this is the second of two rather than the third of three.
   */
  step?: number
  of?: number
  /**
   * What the small line above the heading says, when "step 3 of 3" is not the
   * truth. On the landing page the tutorial gates nothing and is numbered
   * against nothing, so it names itself instead.
   */
  lead?: string
  /** A returning player: the skip is the primary action rather than the small one. */
  seen?: boolean
  /**
   * Run the kitchen rather than illustrate it.
   *
   * Off by default so the `lobby` variant — and any test that mounts this —
   * stays the cheap, keyless, still-picture card it always was. `App.tsx` turns
   * it on for the gate, which is the one place a player has a keyboard free.
   */
  interactive?: boolean
  /**
   * Hand off to a timed solo round when the steps are done.
   *
   * The steps teach the verbs and deliberately have no clock on them; the
   * practice round is where the clock, the ticket queue and an unstaged fire
   * are met for the first time. Off by default, because the lobby's reading
   * cards must not start a five-minute game beside a partner who is waiting.
   */
  practice?: boolean
  /**
   * Study mode: the lesson cannot be skipped. There is no skip button, and Next
   * only becomes available once the step's own goal has been done in the
   * kitchen, so every participant arrives at a round having played every card.
   * Off by default, which is every caller that predates study mode.
   */
  forced?: boolean
  /**
   * The card to open on (0-based), read once at mount — the study page resumes
   * a refreshed tab where it was. The kitchen is staged for that card exactly
   * as if the player had walked there.
   */
  initialStep?: number
  /** Told the card on screen whenever it changes. */
  onStepChange?(index: number): void
  onDone?(skipped: boolean): void
}

export function Tutorial({
  steps,
  catalog,
  dishes,
  variant = 'gate',
  step: gateStep = 3,
  of = 3,
  lead,
  seen = false,
  interactive = false,
  practice = false,
  forced = false,
  initialStep = 0,
  onStepChange,
  onDone,
}: TutorialProps) {
  // Two screens, one after the other: the cards, then the round. The kitchen
  // behind the cards is torn down when the round starts, because two sandboxes
  // both binding `window` for the space bar is one of them not working.
  const [phase, setPhase] = useState<'steps' | 'practice'>('steps')
  const playable = interactive && steps.length > 0 && phase === 'steps'
  const sandbox = useSandbox(steps, catalog, playable, initialStep)
  // One index, two owners. When the kitchen is running it is the kitchen's —
  // a step ends because the engine said so — and the buttons ask the sandbox
  // to move rather than moving a number behind its back.
  const [readingIndex, setReadingIndex] = useState(() => Math.max(0, Math.min(initialStep, steps.length - 1)))
  const index = playable ? sandbox.index : readingIndex
  const goTo = playable ? sandbox.goTo : setReadingIndex
  const restart = playable ? sandbox.restart : (): void => setReadingIndex(0)

  const at = Math.min(index, Math.max(0, steps.length - 1))
  const step = steps[at]
  // A goal-driven final step finishes inside the sandbox. The success card
  // after it is deliberately read-only, so it is ready to leave immediately.
  const complete = playable && (sandbox.finished || (at >= steps.length - 1 && step?.goal === undefined))
  const { tick, ticks } = useAnimationTick(
    (step?.animated ?? false) && !playable && phase === 'steps',
  )
  const last = at >= steps.length - 1

  const onStepChangeRef = useRef(onStepChange)
  onStepChangeRef.current = onStepChange
  useEffect(() => {
    if (phase === 'steps') onStepChangeRef.current?.(at)
  }, [at, phase])

  if (!step) return null

  const finish = (skipped: boolean): void => {
    rememberTutorial(skipped)
    onDone?.(skipped)
  }

  // The cards are over and the clock is on. Nothing of this component is left
  // on screen: the practice round is the whole page, HUD and all, because a
  // round rendered inside a lesson card would be a smaller game than the one
  // it is practising for.
  if (phase === 'practice') {
    return <PracticeRound catalog={catalog} onDone={() => finish(false)} />
  }

  return (
    <section className={`gate gate-tutorial${variant === 'lobby' ? ' gate-tutorial-lobby' : ''}`}>
      <div className="tutorial-copy">
        <p className="gate-step">
          {lead ?? (variant === 'gate' ? `Step ${gateStep} of ${of}` : 'While you wait')} · {at + 1} of{' '}
          {steps.length}
        </p>
        <h1>{step.title}</h1>
        {variant === 'gate' && seen && at === 0 && (
          <p className="hint tutorial-returning">
            You've done this before. Skip ahead whenever you like.
          </p>
        )}
        <p className="hint">{step.body}</p>
      </div>

      {playable && (
        <div className="tutorial-stage">
          <canvas
            ref={sandbox.canvasRef}
            className="tutorial-canvas"
            role="img"
            aria-label="Practice kitchen"
          />
          {/* The instruction, not the rule: the paragraph above says why, this
              says which key. Named keys are drawn as the physical thing rather
              than quoted in prose, because "hold SPACE" read as text is what
              players have been reporting as a control that does not work. */}
          <p className="tutorial-instruction" role="status">
            <span className="tutorial-keys" aria-hidden="true">
              {step.keys.map((key) => (
                <kbd key={key} className={key.length > 1 ? 'kbd-wide' : undefined}>
                  {key}
                </kbd>
              ))}
            </span>
            <span className="tutorial-said">{step.instruction}</span>
          </p>
          <p className="tutorial-cue">
            {step.goal
              ? sandbox.finished && !last
                ? step.pauseAfterGoal
                  ? 'The pan is safe. Press Next for the fire drill.'
                  : 'Done. Press Next when you are ready.'
                : last
                ? complete
                  ? "That's everything. Pick what to do next."
                  : 'Serve the dish on the order to finish.'
                : 'Do it and the next card appears.'
              : "That's everything. Pick what to do next."}
          </p>
        </div>
      )}

      <div className="tutorial-icons">
        {step.draw.map((spec, i) => (
          <Fragment key={`${step.id}-${i}-${specKey(spec)}`}>
            {step.id === 'clocks' && i > 0 && <span className="tutorial-flow-arrow" aria-hidden="true">→</span>}
            <figure className="tutorial-icon">
              <SpecIcon spec={spec} catalog={catalog} stepId={step.id} tick={tick} ticks={ticks} />
              <figcaption>{captionFor(spec, catalog)}</figcaption>
            </figure>
          </Fragment>
        ))}
      </div>

      {step.dish && <TicketRoutes dishId={step.dish} dishes={dishes} catalog={catalog} />}

      {/* Where you are in the reading, as marks rather than as a sentence —
          the same "count it, do not read it" rule the plate stack follows. */}
      <ol className="tutorial-dots" aria-hidden="true">
        {steps.map((s, i) => (
          <li key={s.id} className={i === at ? 'tutorial-dot tutorial-dot-here' : 'tutorial-dot'} />
        ))}
      </ol>

      {/* Two shapes of ending, because there are two shapes of screen.
          The kitchen still advances itself when its engine goal fires, but the
          pager is deliberately available while debugging or revisiting a
          lesson. `goToStep` creates a fresh, staged kitchen for the selected
          step, so moving backward cannot leave the next card running a stale
          kitchen action. The lobby remains the ordinary reading-card pager. */}
      {playable ? (
        <div className="gate-actions tutorial-actions">
          <button type="button" onClick={() => goTo(at - 1)} disabled={at === 0}>
            Back
          </button>
          {last ? (
            <>
              {/* The lesson taught the verbs. The round after it is where the
                  clock, the queue and an unstaged fire are met, and it is the
                  difference between having read the game and having played it. */}
              <button
                type="button"
                className="tutorial-next"
                onClick={() => (practice ? setPhase('practice') : finish(false))}
                disabled={!complete}
              >
                {practice ? 'Try a practice round' : 'Ready to play'}
              </button>
              {practice && (
                <button type="button" onClick={() => finish(false)} disabled={!complete}>
                  Skip practice
                </button>
              )}
              {/* Asked for, never automatic: a lesson that restarted itself
                  under somebody who had just finished it would be the screen
                  disagreeing with them. */}
              <button type="button" onClick={restart}>
                Start over
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="tutorial-next"
                onClick={() => goTo(at + 1)}
                disabled={forced && step.goal !== undefined && !sandbox.finished}
              >
                Next
              </button>
              {!forced && (
                <button type="button" className="tutorial-skip" onClick={() => finish(true)}>
                  {seen ? 'Skip' : 'Skip tutorial'}
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="gate-actions tutorial-actions">
          <button type="button" onClick={() => goTo(at - 1)} disabled={at === 0}>
            Back
          </button>
          {!last && (
            <button type="button" className="tutorial-next" onClick={() => goTo(at + 1)}>
              Next
            </button>
          )}
          {variant === 'gate' && last && (
            <button type="button" className="tutorial-next" onClick={() => finish(false)}>
              Done
            </button>
          )}
          {variant === 'lobby' && last && (
            <button type="button" onClick={() => goTo(0)}>
              Start over
            </button>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * The kitchen, running.
 *
 * Three things live here and nowhere else: the clock, the keys, and the bridge
 * from engine events into the `EventFeed` the renderer reads. The rules are in
 * `tutorialSandbox.ts`, which is pure, so what is left in this hook is exactly
 * the part a test could not have asserted on anyway.
 *
 * The clock catches up against wall time rather than trusting
 * `requestAnimationFrame` to fire at 50 Hz, and caps the catch-up, so a tab that
 * was in the background for a minute resumes rather than simulating a minute of
 * kitchen in one frame — the same guard `transport/loop.ts` has for the same
 * reason.
 *
 * `prefers-reduced-motion` deliberately does *not* stop this loop. It stops
 * decorative motion; a kitchen that will not move is not a reduced tutorial, it
 * is no tutorial. The still icons above it are still frozen at tick zero.
 */
const MAX_CATCH_UP_TICKS = 10

interface SandboxHandle {
  canvasRef: React.RefObject<HTMLCanvasElement>
  index: number
  finished: boolean
  goTo(index: number): void
  /** A new kitchen from the first step. */
  restart(): void
}

function useSandbox(steps: TutorialStep[], catalog: Catalog, active: boolean, initialStep = 0): SandboxHandle {
  // Read when the kitchen is first built only: the step a player moves on to
  // is the sandbox's own business after that.
  const initialStepRef = useRef(initialStep)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Through a ref, not through the effect's deps: the catalog arrives with
  // `/api/config` and re-mounting the kitchen underneath a player because a
  // dish list landed would be a reset they did not ask for.
  const catalogRef = useRef(catalog)
  catalogRef.current = catalog
  const sandboxRef = useRef<Sandbox | null>(null)
  const feedRef = useRef<EventFeed | null>(null)
  const pendingRef = useRef<PlayerAction[]>([])
  const [index, setIndex] = useState(0)
  const [finished, setFinished] = useState(false)

  /** A keyframe on demand: the entry into seeing the kitchen at all. */
  const publish = useCallback((sandbox: Sandbox, keyframe: boolean): void => {
    const feed = feedRef.current
    if (!feed) return
    const events: GameEvent[] = keyframe
      ? [
          {
            tick: sandbox.state.tick,
            wallClock: Date.now(),
            actor: 'system',
            type: 'KEYFRAME',
            state: serialise(sandbox.state),
          },
          ...sandbox.events,
        ]
      : sandbox.events
    if (events.length === 0) return
    feed.push({ tick: sandbox.state.tick, events })
  }, [])

  const goTo = useCallback(
    (next: number): void => {
      const current = sandboxRef.current
      if (!current) return
      const moved = goToStep(current, steps, next)
      sandboxRef.current = moved
      pendingRef.current = []
      setIndex(moved.index)
      setFinished(moved.finished)
      // Navigation creates a new staged kitchen without emitting a single
      // event, so the fold has no way to follow it. Keyframe, exactly as the
      // server does for a mid-round arrival.
      publish(moved, true)
    },
    [steps, publish],
  )

  /**
   * Start again, properly.
   *
   * Replaying the tutorial means the kitchen the first player walked into, so
   * this rebuilds the first staged state rather than preserving any progress.
   */
  const restart = useCallback((): void => {
    const fresh = createSandbox(steps)
    sandboxRef.current = fresh
    pendingRef.current = []
    setIndex(fresh.index)
    setFinished(fresh.finished)
    publish(fresh, true)
  }, [steps, publish])

  // The kitchen and its painter. Torn down and rebuilt if the step list ever
  // changes identity, which in practice it does not once the config has landed.
  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return

    const feed = new EventFeed()
    feedRef.current = feed
    const startAt = Math.max(0, Math.min(initialStepRef.current, steps.length - 1))
    const sandbox = createSandbox(steps, startAt)
    sandboxRef.current = sandbox
    setIndex(sandbox.index)
    setFinished(sandbox.finished)
    publish(sandbox, true)

    // The spotlight, recomputed only when the step changes: which tiles a step
    // is about depends on the step and on what has been revealed, and neither
    // moves between steps — so a fifty-times-a-second sweep of the station
    // table would be fifty times the work for the same answer.
    let litFor = -1
    let lit: Cell[] = []

    const renderer = startRenderer(canvas, feed, {
      getSelfId: () => SANDBOX_PLAYER_ID,
      getCatalog: () => catalogRef.current,
      getSpotlight: () => {
        const current = sandboxRef.current
        if (!current) return null
        const step = steps[current.index]
        if (!step) return null
        if (litFor !== current.index) {
          litFor = current.index
          lit = focusCells(step.focus, current.state)
        }
        return lit
      },
    })

    let raf = 0
    let previous = performance.now()
    let owed = 0
    let sinceKeyframe = 0

    const frame = (): void => {
      const now = performance.now()
      owed += now - previous
      previous = now
      let due = Math.floor(owed / TICK_MS)
      if (due > MAX_CATCH_UP_TICKS) {
        owed = 0
        due = MAX_CATCH_UP_TICKS
      } else {
        owed -= due * TICK_MS
      }

      for (let i = 0; i < due; i += 1) {
        const before = sandboxRef.current
        if (!before) break
        const actions = pendingRef.current
        pendingRef.current = []
        const after = advanceSandbox(before, steps, actions)
        sandboxRef.current = after
        sinceKeyframe += 1
        const stepped = after.index !== before.index
        const keyframe = stepped || sinceKeyframe >= KEYFRAME_INTERVAL_TICKS
        if (keyframe) sinceKeyframe = 0
        publish(after, keyframe)
        if (stepped) setIndex(after.index)
        if (after.finished !== before.finished) setFinished(after.finished)
      }

      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      renderer.stop()
      feedRef.current = null
      sandboxRef.current = null
    }
  }, [active, steps, publish])

  // The keys, and only while the kitchen is up. Bound on `window` rather than
  // on the canvas so nobody has to click it first to be able to move — a focus
  // ring the player has to find is a tutorial that starts by failing.
  useEffect(() => {
    if (!active) return

    const onKeyDown = (event: KeyboardEvent): void => {
      const direction = SANDBOX_KEYS[event.key]
      if (direction) {
        event.preventDefault()
        pendingRef.current.push({ kind: 'move', direction })
        return
      }
      if (event.key === ' ') {
        event.preventDefault()
        if (!event.repeat) pendingRef.current.push({ kind: 'interact' })
      }
    }
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === ' ') pendingRef.current.push({ kind: 'interact_release' })
    }
    // A window that loses focus never delivers the keyup. Let go on the
    // player's behalf, exactly as `App.tsx` does during a round.
    const onBlur = (): void => {
      pendingRef.current.push({ kind: 'interact_release' })
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [active])

  return { canvasRef, index, finished, goTo, restart }
}

/**
 * The animation clock, in ticks.
 *
 * Two values, deliberately. The ref moves every frame and is what the drawing
 * actually reads, so a flame is drawn at the tick it is redrawn on rather than
 * at a stale one. React sees the second value, which bumps once every four
 * frames — enough for a flame to look like a flame, and few enough that
 * reconciliation is not doing fifty passes a second over a card with a dozen
 * small canvases on it. A step with nothing moving in it, and every step under
 * `prefers-reduced-motion`, never starts the loop at all.
 */
function useAnimationTick(animated: boolean): { tick: number; ticks: React.MutableRefObject<number> } {
  const [tick, setTick] = useState(0)
  const ticks = useRef(0)
  const reduced = usePrefersReducedMotion()
  const running = animated && !reduced

  useEffect(() => {
    if (!running) {
      ticks.current = 0
      setTick(0)
      return
    }
    let raf = 0
    let frame = 0
    const loop = (): void => {
      ticks.current += 1
      frame += 1
      if (frame % 4 === 0) setTick(ticks.current)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [running])

  return { tick: running ? tick : 0, ticks }
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(query.matches)
    const onChange = (): void => setReduced(query.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])
  return reduced
}

/** The last step's lesson is a real ticket, so it is drawn as one. */
function TicketRoutes({
  dishId,
  dishes,
  catalog,
}: {
  dishId: string
  dishes: DishSummary[]
  catalog: Catalog
}) {
  const dish = dishes.find((d) => d.id === dishId)
  const routes = useMemo(() => (dish ? routesFor(dish.requires) : []), [dish])
  if (!dish || routes.length === 0) return null

  return (
    <div className="tutorial-ticket">
      <span className="ticket-route-cap">{dish.label}, step by step</span>
      {/* Not `.ticket-route`: the docket's strip collapses on a narrow
          viewport, where the gate has a whole screen and does not have to. */}
      <ol className="tutorial-routes">
        {routes.map((route) => (
          <li key={routeKey(route)}>
            <ol className="ticket-route-row" aria-label="Steps, in order">
              {route.map((rstep, i) => (
                <li className="ticket-route-step" key={`${routeKey([rstep])}-${i}`}>
                  {i > 0 && <span className="ticket-route-chevron" aria-hidden="true" />}
                  <RouteIcon step={rstep} catalog={catalog} size={30} />
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ol>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawing one spec with the kitchen's own code
// ---------------------------------------------------------------------------

const TILE = 96

function SpecIcon({
  spec,
  catalog,
  stepId,
  tick,
  ticks,
}: {
  spec: DrawSpec
  catalog: Catalog
  stepId: string
  /** The throttled value: it is in the signature, so it is what triggers a redraw. */
  tick: number
  /** The frame-accurate value: it is what the redraw reads. */
  ticks: React.MutableRefObject<number>
}) {
  const title = captionFor(spec, catalog)

  return (
    <IconCanvas
      className="tutorial-tile"
      size={TILE}
      signature={`tut:${stepId}:${specKey(spec)}:${tick}`}
      title={title}
      draw={(ctx, size) => paint(ctx, size, spec, catalog, ticks.current)}
    />
  )
}

/**
 * One picture, drawn the way the kitchen draws it.
 *
 * A `station` spec goes through `drawStation` itself — bench surface, fixture,
 * whatever is standing on it, the progress bar and its label, the warning ring,
 * the fire overlay — which is why a hob mid-cook here is pixel-for-pixel the hob
 * the player is about to be looking at. Everything else is drawn onto a floor
 * tile, because these functions are all built to sit on the kitchen's warm sand
 * rather than on a dark card.
 */
function paint(
  ctx: CanvasRenderingContext2D,
  size: number,
  spec: DrawSpec,
  catalog: Catalog,
  atTick: number,
): void {
  if (spec.kind === 'station') {
    drawStation(ctx, stationFor(spec, atTick), { x: 0, y: 0, size }, atTick, catalog)
    return
  }

  floor(ctx, size)
  const cx = size / 2
  const cy = size / 2
  const r = size * 0.32

  if (spec.kind === 'ingredient') {
    drawIngredient(ctx, catalog.ingredient(spec.id), cx, cy, r, atTick)
    return
  }
  if (spec.kind === 'container') {
    drawContainer(ctx, container(spec.container, spec.contents ?? [], spec.cookState ?? 'empty', atTick), cx, cy, r, catalog, atTick)
    return
  }
  if (spec.kind === 'plate') {
    drawPlate(ctx, plate(!spec.dirty, spec.contents ?? []), cx, cy, r, catalog, atTick)
    return
  }
  if (spec.kind === 'equipment') {
    drawExtinguisher(ctx, cx, cy, r)
    return
  }
  drawFlame(ctx, cx, cy + r * 0.6, r, atTick)
}

/** The kitchen's floor, so an icon drawn for a tile is standing on one. */
function floor(ctx: CanvasRenderingContext2D, size: number): void {
  ctx.save()
  ctx.beginPath()
  roundRect(ctx, 0, 0, size, size, size * 0.09)
  ctx.fillStyle = COLOURS.floor
  ctx.fill()
  ctx.strokeStyle = 'rgba(120, 96, 62, 0.35)'
  ctx.lineWidth = 1
  ctx.stroke()
  ctx.restore()
}

/**
 * A station, synthesised.
 *
 * The cook phase is a loop: `atTick` runs on forever and the phase is a hundred
 * and twenty ticks wide, so a cooking bar fills, reaches its notch and starts
 * again — a demonstration of a clock rather than a photograph of one. A burnt
 * pan is given a running third clock too, because "this is about to catch fire"
 * is the state worth showing moving.
 */
const PHASE_TICKS = 120

function stationFor(
  spec: Extract<DrawSpec, { kind: 'station' }>,
  atTick: number,
): Station {
  return {
    type: spec.station,
    cell: { x: 0, y: 0 },
    item: null,
    progressTicks: 0,
    progressTargetTicks: 0,
    resumedAtTick: null,
    heldBy: null,
    heldSubject: null,
    container: spec.container
      ? container(spec.container, spec.contents ?? [], spec.cookState ?? 'empty', atTick)
      : null,
    onFire: spec.onFire ?? false,
    dispenses: spec.dispenses ?? null,
    cleanPlates: spec.station === 'plate_stack' ? 3 : 0,
    // A return with washing up on it, so the step that teaches the plate cycle
    // draws the pile it is talking about rather than an empty shelf.
    dirtyPlates: spec.station === 'plate_return' ? 1 : 0,
  }
}

function container(
  kind: string,
  contents: string[],
  cookState: Container['cookState'],
  atTick: number,
): Container {
  const running = cookState === 'cooking' || cookState === 'done' || cookState === 'burnt'
  const start = Math.floor(atTick / PHASE_TICKS) * PHASE_TICKS
  return {
    id: `tut-${kind}`,
    kind,
    contents: [...contents],
    cookState,
    phaseStartedAtTick: running ? start : null,
    phaseEndsAtTick: running ? start + PHASE_TICKS : null,
  }
}

const plate = (clean: boolean, contents: string[]): Plate => ({
  id: 'tut-plate',
  clean,
  contents: [...contents],
})

// ---------------------------------------------------------------------------
// Naming the pictures
// ---------------------------------------------------------------------------

/** What this picture is, in words: the caption, and the screen reader's copy. */
function captionFor(spec: DrawSpec, catalog: Catalog): string {
  if (spec.kind === 'ingredient') return catalog.ingredient(spec.id).label
  if (spec.kind === 'container') return catalog.containerLabel(spec.container)
  if (spec.kind === 'plate') return spec.dirty ? 'Dirty plate' : 'Clean plate'
  if (spec.kind === 'equipment') return 'Fire extinguisher'
  if (spec.kind === 'fire') return 'Fire'
  return stationCaption(spec)
}

function stationCaption(spec: Extract<DrawSpec, { kind: 'station' }>): string {
  const name = stationName(spec.station as StationType)
  if (spec.onFire) return `${name}, on fire`
  if (spec.cookState === 'cooking') return `${name}, cooking`
  if (spec.cookState === 'done') return `${name}, ready`
  if (spec.cookState === 'burnt') return `${name}, burnt`
  return name
}

/** A stable key for a spec: React lists, and the redraw signature. */
function specKey(spec: DrawSpec): string {
  if (spec.kind === 'ingredient') return `ingredient:${spec.id}`
  if (spec.kind === 'container') return `container:${spec.container}:${spec.cookState ?? 'empty'}`
  if (spec.kind === 'plate') return `plate:${spec.dirty ? 'dirty' : 'clean'}:${(spec.contents ?? []).join('+')}`
  if (spec.kind === 'equipment') return `equipment:${spec.id}`
  if (spec.kind === 'fire') return 'fire'
  return `station:${spec.station}:${spec.container ?? ''}:${spec.cookState ?? ''}:${spec.onFire ? 'fire' : ''}`
}
